import Box from "@/common/components/Box";
import { ShowHeader } from "@/common/components/ShowHeader";
import useCompany from "@/common/hooks/queries/useCompany";
import useBankingDeposit from "@/features/privateCompanies/hooks/bankingDeposits/useBankingDeposit";
import { ChevronsRightIcon } from "lucide-react";
import { useParams } from "react-router-dom";
import BankingDepositStatus from "./components/BankingDepositStatus";
import BankingDepositResume from "./components/BankingDepositResume";
import BankingDepositData from "./components/BankingDepositData";
import PixInstructions from "./components/PixInstructions";
import PixCopyAndPaste from "./components/PixCopyAndPaste";
import { useEffect, useState } from "react";
import ConfettiOnPayment from "./components/ConfettiOnPayment";
import BankingDepositFiscalDocuments from "./components/BankingDepositFiscalDocuments";
import { isAfter } from "date-fns";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";

const BankingDepositsShow = () => {
  const { companyId, id } = useParams();

  const [bankingDepositRefetchInterval, setBankingDepositRefetchInterval] = useState<number>();

  const {
    data: company,
    isPending: isCompanyPending,
  } = useCompany({ id: companyId || '' });

  const {
    data: bankingDeposit,
    isPending: isBankingDepositPending,
    error: bankingDepositError = {} as any,
    refetch: refetchBankingDeposit,
  } = useBankingDeposit({
    id: id || '',
    refetchInterval: bankingDepositRefetchInterval,
  });

  const isExpiredDateBeforeNow = bankingDeposit?.payment?.source?.expires_at &&
    isAfter(new Date(bankingDeposit.payment.source.expires_at), new Date());
  const shouldDisplayPaymentSource = bankingDeposit?.status === "pending" && isExpiredDateBeforeNow;
  const isPaymentMethodPix = bankingDeposit?.payment?.source?.copy_and_paste;

  useEffect(() => {
    if (bankingDeposit?.status === "pending") {
      return setBankingDepositRefetchInterval(10 * 1000);
    }

    return setBankingDepositRefetchInterval(undefined);
  }, [bankingDeposit?.status]);

  return (
    <>
      <div>
        <ShowHeader
          goBack
          goBackProps={{
            fallback: `/companies/${companyId}/banking-deposits`,
            searchLastRouteFromFallback: true
          }}
          isLoading={isCompanyPending}
          titleClassName="font-normal text-md text-white-400"
          title={company?.general_information?.fantasy_name}
          subtitle={(
            <div className="flex items-center gap-2">
              <h2 className="text-white-400">
                Depósitos
              </h2>

              <ChevronsRightIcon className="text-white-400" />

              <h2 className="text-white-800">
                Detalhes do Depósito
              </h2>
            </div>
          )}
        />

        <ErrorMessageWrapper
          className="h-[calc(100vh-140px)]"
          statusCode={company?.kind === "public_company" ? 404 : bankingDepositError?.code}
          refetchFunction={refetchBankingDeposit}
          notFoundFallback={`/companies/${companyId}/banking-deposits`}
          notFoundButtonText="Voltar para Depósitos"
        >
          <div className="flex flex-col gap-6">
            <BankingDepositData
              bankingDeposit={bankingDeposit}
              isLoading={isBankingDepositPending}
            />

            <Box className="flex gap-6 shadow-lg max-h-[calc(100vh-340px)] overflow-auto">
              <div className="flex flex-col gap-6 flex-1">
                <BankingDepositStatus
                  status={bankingDeposit?.status}
                  isLoading={isBankingDepositPending}
                />

                <BankingDepositResume
                  bankingDeposit={bankingDeposit}
                  isLoading={isBankingDepositPending}
                />

                {shouldDisplayPaymentSource && isPaymentMethodPix && (
                  <PixInstructions />
                )}
              </div>

              {shouldDisplayPaymentSource && isPaymentMethodPix && (
                <PixCopyAndPaste
                  bankingDeposit={bankingDeposit}
                />
              )}
            </Box>

            {bankingDeposit && <BankingDepositFiscalDocuments bankingDeposit={bankingDeposit} />}
          </div>
        </ErrorMessageWrapper>
      </div>

      {bankingDeposit && <ConfettiOnPayment bankingDeposit={bankingDeposit} />}
    </>
  );
}

export default BankingDepositsShow;
